/* Keep SC Product Options responsible for decal values, validation and cart submission. */
(() => {
  const propertyNames = ['NHL Shield Logo', 'NHL Team Logo Decals'];
  const excludedTeamChoices = new Set(['no team decals']);
  const isExcludedTeamChoice = option =>
    excludedTeamChoices.has(option.textContent.trim().replace(/\s+/g, ' ').toLowerCase());
  const instances = new Map();
  const preferences = new WeakMap();

  function decalOptions(form) {
    const app = window.BOLD?.options?.app;
    const product = app?.getOptionProductByForm?.(form);
    if (!Array.isArray(product?.optionSets)) return [];
    return product.optionSets.flatMap(set => set.options).filter(option =>
      propertyNames.includes(option.publicName) && !option.isPricedOption &&
      typeof option.hide === 'function' && typeof option.show === 'function' &&
      typeof option.isVisible === 'function' &&
      form.contains(option.DOM?.element)
    );
  }

  // Reuse the theme's custom-select keyboard, typeahead and selection behavior.
  // Keep the app-owned select as the sole submitted field.
  function teamPicker(select, dialog) {
    if (!window.jQuery || !select.options.length) return null;
    const root = document.createElement('div');
    root.className = 'cc-select nhl-team-select';
    root.id = `${select.id || dialog.id || dialog.getAttribute('aria-labelledby')}-team-picker`;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cc-select__btn';
    button.id = `${root.id}-button`;
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    const labels = Array.from(select.labels || []);
    button.setAttribute('aria-label', 'NHL team logo decals');
    const text = document.createTextNode(select.selectedOptions[0]?.textContent || 'Choose NHL team');
    button.appendChild(text);
    const list = document.createElement('ul');
    list.className = 'cc-select__listbox';
    list.id = `${root.id}-list`;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', button.getAttribute('aria-label'));
    list.setAttribute('aria-hidden', 'true');
    list.tabIndex = -1;
    list.hidden = true;
    button.setAttribute('aria-controls', list.id);
    const signature = () => JSON.stringify(Array.from(select.options, option => [option.value, option.textContent, option.disabled]));
    const initialOptions = signature();
    Array.from(select.options).forEach((option, index) => {
      if (isExcludedTeamChoice(option)) return;
      const item = document.createElement('li');
      item.className = `cc-select__option js-option${option.disabled ? ' is-disabled' : ''}`;
      item.id = `${root.id}-option-${index}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(option.selected));
      item.setAttribute('aria-disabled', String(option.disabled));
      item.dataset.value = option.value;
      const label = document.createElement('span');
      label.textContent = option.textContent;
      item.appendChild(label);
      list.appendChild(item);
    });
    root.append(button, list);
    // App markup wraps the native field in a label; keep interactive list outside
    // that label so its default click action cannot steal focus and close the list.
    (select.closest('label') || select).after(root);
    window.jQuery(document).trigger('cc:component:load', ['custom-select', root.parentElement]);
    if (!root.classList.contains('cc-initialized')) { root.remove(); return null; }
    const events = new AbortController();
    const listen = (target, name, handler, capture = false) => target.addEventListener(name, handler, { signal: events.signal, capture });
    const close = () => { if (root.classList.contains('is-open')) list.dispatchEvent(new Event('blur')); };
    // The theme uses mousedown for pointer activation; support Enter/AT clicks too.
    listen(button, 'click', event => {
      if (event.detail === 0 && !root.classList.contains('is-open')) {
        button.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, button: 0 }));
      }
    });
    listen(list, 'click', event => {
      if (event.target.closest('[aria-disabled="true"]')) event.stopImmediatePropagation();
    }, true);
    listen(root, 'change', event => {
      if (!event.detail || select.value === event.detail.selectedValue) return;
      select.value = event.detail.selectedValue;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
    const sync = () => {
      const selected = list.querySelector('[aria-selected="true"]');
      if (selected?.dataset.value !== select.value) {
        root.dispatchEvent(new CustomEvent('selectOption', { detail: { value: select.value } }));
      }
      // App resets may restore the native field's original attributes.
      select.classList.add('nhl-team-select-native');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      button.disabled = select.disabled;
      button.setAttribute('aria-label', `NHL team logo decals: ${select.selectedOptions[0]?.textContent || 'Choose NHL team'}`);
    };
    listen(select, 'change', sync);
    // Keep the overlay within the dialog's visible area without changing its layout.
    const positionList = () => {
      const field = button.getBoundingClientRect();
      const bounds = dialog.getBoundingClientRect();
      const above = field.top - Math.max(bounds.top, 0) - 12;
      const below = Math.min(bounds.bottom, window.innerHeight) - field.bottom - 12;
      const preferredHeight = Math.min(240, window.innerHeight * .3);
      const openAbove = below < preferredHeight && above > below;
      root.dataset.placement = openAbove ? 'above' : 'below';
      root.style.setProperty('--nhl-team-list-height', `${Math.floor(Math.min(preferredHeight, Math.max(44, openAbove ? above : below)))}px`);
    };
    listen(button, 'mousedown', positionList);
    listen(root, 'keydown', positionList, true);
    const announceFocusedOption = () => {
      const focused = list.querySelector('.is-focused');
      if (focused) list.setAttribute('aria-activedescendant', focused.id);
    };
    listen(list, 'focus', () => { positionList(); announceFocusedOption(); });
    // The theme moves its visual focus class without updating aria-activedescendant.
    listen(root, 'keydown', announceFocusedOption);
    listen(root, 'mouseover', announceFocusedOption);
    listen(dialog, 'scroll', close);
    listen(window, 'resize', close);
    listen(dialog, 'close', close);
    listen(dialog, 'pointerdown', event => { if (!root.contains(event.target)) close(); });
    const labelTargets = labels.map(label => label.getAttribute('for'));
    labels.forEach(label => label.setAttribute('for', button.id));
    const tabIndex = select.getAttribute('tabindex');
    const ariaHidden = select.getAttribute('aria-hidden');
    select.classList.add('nhl-team-select-native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    sync();
    return {
      element: root,
      sync,
      changed: () => !dialog.contains(root) || initialOptions !== signature(),
      dispose() {
        close();
        events.abort();

        window.jQuery(document).trigger('cc:component:unload', ['custom-select', root.parentElement]);
        root.remove();
        select.classList.remove('nhl-team-select-native');
        if (tabIndex === null) select.removeAttribute('tabindex'); else select.setAttribute('tabindex', tabIndex);
        if (ariaHidden === null) select.removeAttribute('aria-hidden'); else select.setAttribute('aria-hidden', ariaHidden);
        labels.forEach((label, index) => {
          if (labelTargets[index] === null) label.removeAttribute('for'); else label.setAttribute('for', labelTargets[index]);
        });
      }
    };
  }

  function mount(template, form, options) {
    const element = template.content.firstElementChild.cloneNode(true);
    const checkbox = element.querySelector('[type="checkbox"]');
    if (checkbox) checkbox.checked = preferences.get(form) ?? true;
    let includeDecals = checkbox ? checkbox.checked : true;
    const optOut = element.querySelector('[name="properties[NHL Decals]"]');
    const managed = new Set();
    const dialog = element.matches('dialog') ? element : null;
    const positions = new Map();
    const pickers = new Map();
    const restrictedChoices = new Map();
    const events = new AbortController();
    let submitter;
    let approved = false;
    let resuming = false;
    let validating = false;
    let updateDialogButtons = () => {};
    let disposed = false;

    function apply() {
      if (disposed) return;
      // Options can be rebuilt by the app after initial load or a variant change.
      const current = decalOptions(form);
      const controls = [];
      current.forEach((option, index) => {
        const wrapper = option.DOM.element;
        if (dialog && !dialog.contains(wrapper)) {
          const position = document.createComment('NHL decal option position');
          wrapper.before(position);
          positions.set(wrapper, position);
          const slot = option.publicName === 'NHL Shield Logo' ? 'shield' : 'team';
          dialog.querySelector(`[data-nhl-option-slot="${slot}"]`).appendChild(wrapper);
        }
        wrapper.classList.add('nhl-decal-option');
        if (!wrapper.id) wrapper.id = `${checkbox?.id || form.getAttribute('id')}-option-${index}`;
        controls.push(wrapper.id);
        if (!includeDecals) {
          if (wrapper.contains(document.activeElement)) checkbox?.focus();
          if (option.isVisible()) {
            managed.add(option);
            option.hide(); // App skips validation and detaches all submission fields.
          }
          wrapper.hidden = true;
        } else {
          wrapper.hidden = false;
          if (managed.has(option)) {
            managed.delete(option);
            option.show(); // Reattaches the same nodes, retaining the previous choice.
          }
        }
      });
      if (dialog) {
        dialog.querySelectorAll('select[name="properties[NHL Team Logo Decals]"]').forEach(select => {
          let resetSelection = false;
          Array.from(select.options).filter(isExcludedTeamChoice).forEach(option => {
            // Keep app-owned option indexes intact, including the native fallback.
            if (!restrictedChoices.has(option)) {
              restrictedChoices.set(option, { hidden: option.hidden, disabled: option.disabled });
            }
            resetSelection ||= option.selected;
            option.hidden = true;
            option.disabled = true;
          });
          if (resetSelection) {
            select.value = '';
            select.dispatchEvent(new Event('input', { bubbles: true }));
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        pickers.forEach((picker, select) => {
          if (!dialog.contains(select) || picker.changed()) { picker.dispose(); pickers.delete(select); }
        });
        // The app can clone/rebuild option markup after adding to cart. Cloned
        // custom controls have no registered owner/listeners and must not survive.
        const ownedPickers = new Set(Array.from(pickers.values(), picker => picker.element));
        dialog.querySelectorAll('.nhl-team-select').forEach(root => {
          if (!ownedPickers.has(root)) root.remove();
        });
        dialog.querySelectorAll('select[name="properties[NHL Team Logo Decals]"]').forEach(select => {
          if (!pickers.has(select)) {
            const picker = teamPicker(select, dialog);
            if (picker) pickers.set(select, picker);
          }
          pickers.get(select)?.sync();
        });
      }
      checkbox?.setAttribute('aria-controls', controls.join(' '));
      optOut.disabled = includeDecals;
      updateDialogButtons();
    }

    // Keep the dialog outside the app-owned option set, which may be rebuilt.
    if (dialog) form.appendChild(element);
    else options[0].DOM.element.before(element);
    if (dialog) {
      const status = dialog.querySelector('[role="status"]');
      const add = dialog.querySelector('.nhl-decal-dialog__add');
      const skip = dialog.querySelector('.nhl-decal-dialog__skip');
      updateDialogButtons = () => {
        const team = dialog.querySelector('select[name="properties[NHL Team Logo Decals]"]');
        add.disabled = validating || !team?.value || team.selectedOptions[0]?.disabled;
        skip.disabled = validating;
      };
      const listen = (target, event, handler, capture = false) =>
        target.addEventListener(event, handler, { capture, signal: events.signal });
      function open(event, button) {
        event.preventDefault();
        event.stopImmediatePropagation();
        approved = false;
        delete form.dataset.nhlCartRedirect;
        submitter = button;
        status.textContent = '';
        includeDecals = true;
        apply();
        if (!dialog.open) dialog.showModal();
        dialog.querySelector('.nhl-team-select .cc-select__btn, select:not(.nhl-team-select-native), input:not([type="hidden"]), [tabindex="0"]')?.focus();
      }
      listen(form, 'click', event => {
        const button = event.target.closest('button[type="submit"], input[type="submit"]');
        // The app replays its hidden original button after validating the visible clone.
        if (!button || (button.classList.contains('bold_hidden') && approved) || resuming) return;
        open(event, button);
      }, true);
      listen(form, 'submit', event => {
        if (!approved) open(event, event.submitter || form.querySelector('[type="submit"]:not(.bold_hidden)'));
      }, true);
      async function continueToCart(withoutDecals) {
        if (validating || !submitter?.isConnected || submitter.disabled) return;
        if (!withoutDecals && add.disabled) return;
        includeDecals = !withoutDecals;
        apply();
        validating = true;
        add.disabled = skip.disabled = true;
        status.textContent = '';
        try {
          // Use the app's required-option validation while the fields remain visible in the dialog.
          await Promise.all(decalOptions(form).filter(option => option.isVisible()).map(option => option.validateOption()));
          if (!form.reportValidity()) return;
          approved = true;
          form.dataset.nhlCartRedirect = 'true';
          dialog.close();
          resuming = true;
          submitter.click();
        } catch {
          delete form.dataset.nhlCartRedirect;
          status.textContent = 'Please complete the decal selections, or add your rink without decals.';
        } finally {
          resuming = false;
          validating = false;
          updateDialogButtons();
        }
      }
      listen(add, 'click', () => continueToCart(false));
      listen(skip, 'click', () => continueToCart(true));
      listen(dialog.querySelector('.nhl-decal-dialog__close'), 'click', () => dialog.close());
      listen(dialog, 'change', updateDialogButtons);
      listen(dialog, 'close', () => submitter?.focus());
      listen(dialog, 'keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(dialog.querySelectorAll('button, select, input:not([type="hidden"]), [tabindex]'))
          .filter(node => !node.disabled && node.tabIndex >= 0 && node.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
    }
    const onChange = () => {
      includeDecals = checkbox ? checkbox.checked : true;
      if (checkbox) preferences.set(form, includeDecals);
      apply();
    };
    checkbox?.addEventListener('change', onChange);
    const onReset = () => queueMicrotask(onChange);
    form.addEventListener('reset', onReset);
    apply();
    return {
      element, apply,
      dispose() {
        disposed = true;
        events.abort();
        if (dialog) delete form.dataset.nhlCartRedirect;
        pickers.forEach(picker => picker.dispose());
        pickers.clear();
        restrictedChoices.forEach((original, option) => {
          option.hidden = original.hidden;
          option.disabled = original.disabled;
        });
        restrictedChoices.clear();
        if (dialog?.open) dialog.close();
        checkbox?.removeEventListener('change', onChange);
        form.removeEventListener('reset', onReset);
        managed.forEach(option => { option.DOM.element.hidden = false; option.show(); });
        decalOptions(form).forEach(option => option.DOM.element.classList.remove('nhl-decal-option'));
        positions.forEach((position, wrapper) => { if (position.isConnected) position.replaceWith(wrapper); });
        element.remove();
      }
    };
  }

  function refresh() {
    instances.forEach((instance, template) => {
      if (!template.isConnected || !instance.element.isConnected) {
        instance.dispose();
        instances.delete(template);
      }
    });
    document.querySelectorAll('template[data-nhl-decal-opt-in]').forEach(template => {
      const existing = instances.get(template);
      if (existing) { existing.apply(); return; }
      const form = template.closest('form.js-product-form');
      if (!form) return;
      if (template.content.firstElementChild.matches('dialog') && typeof HTMLDialogElement.prototype.showModal !== 'function') return;
      const options = decalOptions(form);
      // Both known free options must exist; leave unrelated/app-incompatible forms alone.
      if (!propertyNames.every(name => options.some(option => option.publicName === name))) return;
      instances.set(template, mount(template, form, options));
    });
  }

  let queued = false;
  function scheduleRefresh() {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; refresh(); });
  }
  const observer = new MutationObserver(scheduleRefresh);
  observer.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scheduleRefresh);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, template) => {
      if (event.target.contains(template)) { instance.dispose(); instances.delete(template); }
    });
  });
  refresh();
})();
